import { MediaLibraryPageConfig } from 'app/config/mediaLibraryPage';
import { AlbumAssignmentFilter } from 'api/media';
import { AlbumDetail } from 'app/modules/mediaLibraryPage/types/AlbumDetail';
import { ProfileGroup } from 'types/Page';
import { UserLastPostsQueryParams } from 'types/User';

/** The browsed album scope: a profile (page) id, or the shared albums. */
export type AlbumScope =
  | number
  | typeof MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE;

export function isSharedScope(
  scope: AlbumScope | undefined,
): scope is typeof MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE {
  return scope === MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE;
}

/** True for 'shared' or a profile id > 0, i.e. a scope albums can be listed for. */
export function isBrowsableScope(
  scope: AlbumScope | undefined,
): scope is AlbumScope {
  return isSharedScope(scope) || (typeof scope === 'number' && scope > 0);
}

export function parseAlbumScope(
  raw: string | null,
  availablePageIds: number[],
): AlbumScope | undefined {
  if (raw === MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE) {
    return MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE;
  }
  if (raw === null || raw === '') return undefined;
  const pageId = Number(raw);
  return Number.isInteger(pageId) && availablePageIds.includes(pageId)
    ? pageId
    : undefined;
}

/** The first profile, in page order, among those selected in the calendar. */
export function pickCalendarProfile(
  availablePageIds: number[],
  calendar:
    | Pick<UserLastPostsQueryParams, 'profiles' | 'profileGroups'>
    | null
    | undefined,
  profileGroups: Pick<ProfileGroup, 'id' | 'pages'>[],
): number | undefined {
  const selectedProfiles = calendar?.profiles;
  const selectedGroups = calendar?.profileGroups;
  // The api does not always send lists here; the calendar guards them the same way.
  const selected = new Set([
    ...(Array.isArray(selectedProfiles) ? selectedProfiles : []),
    ...(Array.isArray(selectedGroups)
      ? profileGroups
          .filter((group) => selectedGroups.includes(group.id))
          .flatMap((group) => group.pages.map((page) => page.id))
      : []),
  ]);

  return availablePageIds.find((id) => selected.has(id));
}

/** The scope an album belongs to; undefined when the user can't see its profile. */
export function scopeOfAlbum(
  album: Pick<AlbumDetail, 'assigned_model'>,
  availablePageIds: number[],
): AlbumScope | undefined {
  if (album.assigned_model === null) {
    return MediaLibraryPageConfig.SHARED_ALBUMS_SCOPE;
  }
  return availablePageIds.includes(album.assigned_model.id)
    ? album.assigned_model.id
    : undefined;
}

export function isAlbumInScope(
  album: Pick<AlbumDetail, 'assigned_model'>,
  scope: AlbumScope,
): boolean {
  return album.assigned_model === null
    ? isSharedScope(scope)
    : album.assigned_model.id === scope;
}

export function albumScopeListParams(scope: AlbumScope): AlbumAssignmentFilter {
  return isSharedScope(scope)
    ? { assignedModelType: 'none' }
    : { assignedModelType: 'page', assignedModelId: scope };
}
